import React, { useEffect, useState } from 'react';
import { useMessage } from '../../hooks/useMessage';
import { BackendPayload, FormsProps } from '../../types/forms';
import { getDecisionCriteria } from '../../api/decisions.api';
import { getFormsResult, submitForm } from '../../api/forms.api';
import { transformFormFields } from '../../utils/forms.utils';
import Loading from '../loading/Loading';
import { Typography } from '@mui/material';
import DynamicForm from '../DynamicForm/DynamicForm';
import { useError } from '../../context/error-context';

const DataUpdateForm = ({ formId, formName, responseId, onCancel, onAfterUpdate }: FormsProps) => {
    const { showMessage, MessageContainer } = useMessage();
    const [ loading, setLoading ] = useState(true);
    const [ submitting, setSubmitting ] = useState(false);
    const [ formAllFields, setFormAllFields ] = React.useState<BackendPayload['fields']>([]);
    const { showError } = useError();
    useEffect(() => {
        if (!formId || !responseId) {
            setLoading(false);
            return;
        }

        Promise.all([
            getDecisionCriteria(formId),
            getFormsResult(formId, responseId),
        ])
               .then(results => {
                   const [ response, response2 ] = results;

                   const values: {
                       [key: string]: any
                   } = {};
                   response2.response.forEach(item => {
                       values[item.id] = item.value;
                   });

                   const elements: any[] = response.form_config.elements;
                   const formattedElements: BackendPayload['fields'] = [];
                   elements.forEach(element => {
                       formattedElements.push(...transformFormFields(element.children, values));
                   });
                   setFormAllFields(formattedElements);
               })
                .catch((err) => {
                showError({
                    code: err.response?.status || 500,
                    message: "API Error",
                    description: "Failed to fetch data",
                    showRefresh: true,
                })
            })
               .finally(() => setLoading(false));
    }, [ formId, responseId , showError]);

    const submitFormHandler = async (values: {
        [key: string]: any
    }) => {
        if (!formId || !responseId) {
            return;
        }

        const formData: {
            form_id: number;
            response_id: number;
            data: {
                id: string;
                value: any;
            }[];
        } = {
            form_id: +formId,
            response_id: +responseId,
            data: [],
        };

        Object.keys(values).forEach((key: string) => {
            formData.data.push({ id: key, value: values[key] });
        });

        setSubmitting(true);
        submitForm(formData)
        .then(() => {
            setSubmitting(false);
            showMessage('You response was updated successfully.', 'success');
            onAfterUpdate?.(formData);
            onCancel && setTimeout(onCancel, 1500);
        })
        .catch((err) => {
                showError({
                    code: err.response?.status || 500,
                    message: "API Error",
                    description: "Failed to fetch data",
                    showRefresh: true,
                })
            })
        .finally(() => setSubmitting(false));
    };

    return (
        <div>
            {loading ? <Loading/> : (
                <div>
                    <Typography variant="h4" gutterBottom>Edit result {responseId}</Typography>
                    <DynamicForm
                        formId={formId}
                        formName={formName}
                        config={{ fields: formAllFields }}
                        onSubmit={submitFormHandler}
                        apiSubmitting={submitting}
                        saveButtonLabel="Save"
                        hasCancelButton
                        onCancel={onCancel}
                    />
                </div>
            )}
            <MessageContainer/>
        </div>
    );
};

export default DataUpdateForm;